import { convertImageToText } from '../../../services/gemini';

const OCR_RENDER_SCALE = 2;

export const loadPdfJs = () => import('pdfjs-dist/webpack.mjs');

function normalizeTextContent(textItems) {
    return textItems
        .map((item) => ('str' in item ? item.str : ''))
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

async function canvasToBlob(canvas, mimeType) {
    if (typeof canvas.toBlob === 'function') {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }

                reject(new Error('Failed to render PDF page for OCR.'));
            }, mimeType);
        });
    }

    throw new Error('Canvas blob conversion is not supported in this browser.');
}

export async function renderPdfPageToImageFile(page, pageNumber) {
    const viewport = page.getViewport({ scale: OCR_RENDER_SCALE });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Canvas rendering is not available for PDF OCR.');
    }

    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    await page.render({
        canvasContext: context,
        viewport,
    }).promise;

    const imageBlob = await canvasToBlob(canvas, 'image/png');
    return new File([imageBlob], `pdf-page-${pageNumber}.png`, { type: 'image/png' });
}

export async function extractPageTextWithOcr(page, pageNumber) {
    const pageImage = await renderPdfPageToImageFile(page, pageNumber);
    const ocrText = await convertImageToText(pageImage);
    return ocrText.replace(/\s+/g, ' ').trim();
}

export async function extractPdfText(pdfFile, options = {}) {
    const {
        loadPdfJsModule = loadPdfJs,
        ocrPage = extractPageTextWithOcr,
    } = options;
    const { getDocument } = await loadPdfJsModule();
    const fileBuffer = await pdfFile.arrayBuffer();
    const loadingTask = getDocument({
        data: fileBuffer,
        disableWorker: true,
    });

    const pdfDocument = await loadingTask.promise;
    const pageTexts = [];

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const pageText = normalizeTextContent(textContent.items);

        if (pageText) {
            pageTexts.push(pageText);
            continue;
        }

        const ocrText = await ocrPage(page, pageNumber);
        if (ocrText) {
            pageTexts.push(ocrText);
        }
    }

    const extractedText = pageTexts.join('\n\n').trim();

    if (!extractedText) {
        throw new Error('No extractable text was found in this PDF, even after OCR fallback.');
    }

    return extractedText;
}